/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { defineMessages, injectIntl } from 'react-intl'

import type { Runner } from '@modules/ui-types'
import {
  OnFilterChange,
  CuiFilterContext,
  createQueryExecutor,
  getFieldValues,
  toggleFieldValue,
} from '@modules/cui/FilterContext'
import type { ControlledFilterQuery } from '@modules/cui/FilterContext'

import { getQueryModel, schema, defaultFields } from './schema'
import { getFilters } from './filters'

import type { WrappedComponentProps } from 'react-intl'
import type { FunctionComponent } from 'react'

const messages = defineMessages({
  searchBarPlaceholder: {
    id: `runner-filter-context.placeholder`,
    defaultMessage: `e.g.: healthy:y beats`,
  },
  emptyMessage: {
    id: `runner-filter-context.no-matches`,
    defaultMessage: `No hosts match your query`,
  },
})

export const executeQuery = createQueryExecutor({
  defaultFields,
  getQueryModel,
})

interface Props extends WrappedComponentProps {
  query: ControlledFilterQuery
  onChange: OnFilterChange<Runner>
  runners?: Runner[]
}

const RunnerFilterContext: FunctionComponent<Props> = ({ intl, query, onChange, runners }) => {
  const { formatMessage } = intl
  const filters = getFilters({
    intl,
    runners,
  })
  const placeholder = formatMessage(messages.searchBarPlaceholder)
  const emptyMessage = formatMessage(messages.emptyMessage)

  return (
    <CuiFilterContext<Runner>
      query={query}
      onChange={onChange}
      records={runners}
      schema={schema}
      filters={filters}
      executeQuery={executeQuery}
      placeholder={placeholder}
      emptyMessage={emptyMessage}
    />
  )
}

export function getFilterTags({ query, field }: { query: ControlledFilterQuery; field: string }) {
  return getFieldValues({ query, schema, field })
}

export function toggleField({
  query,
  field,
  value,
}: {
  query: ControlledFilterQuery
  field: string
  value: string
}) {
  return toggleFieldValue({
    query,
    schema,
    field,
    value,
  })
}

export default injectIntl(RunnerFilterContext)
